/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useState } from "react";
import {
  Box,
  Button,
  FlexContainer,
  Message,
  ModalConfirm,
  Spacer,
  Table,
  Tag,
  theme,
  Text,
} from "@nordcloud/gnui";
import {
  useGlobalTagsQuery,
  useUpdateGlobalKeysMutation,
} from "~/generated/graphql";
import { BreadcrumbsBox, TableLoader, UniversalWrap } from "~/components";
import { ERROR_TEXT } from "~/constants";
import { ACTIONS, Can } from "~/services/auth";
import { showError, showSuccess } from "~/services/toast";
import { generateActionConfirmText, generateActionSuccessText } from "~/tools";
import { MappingRuleSideButton } from "./AddMappingRule";

type DeleteData = {
  type: string;
  name: string;
};

export function MappingRulesPage() {
  const [isModalConfirmOpen, setIsModalConfirmOpen] = useState(false);
  const [deleteData, setDeleteData] = useState<DeleteData>({
    type: "",
    name: "",
  });

  const { data, loading, error, refetch } = useGlobalTagsQuery();
  const [updateKeys] = useUpdateGlobalKeysMutation();

  const onSubmit = async (formData: DeleteData) => {
    setIsModalConfirmOpen(false);

    try {
      await removeKey(formData.type)(formData.name);

      showSuccess(
        generateActionSuccessText(`The Mapping Rule ${deleteData.name}`)()(
          "removed"
        )()
      );
    } catch {
      showError(ERROR_TEXT.default);
    } finally {
      void refetch();
    }
  };

  const removeKey = (kind: string) => (value: string) => {
    const { application = [], environment = [] } = data?.globalTagKeys || {};

    if (kind === "application") {
      return updateKeys({
        variables: {
          application: application.filter((key) => key !== value),
          environment,
        },
      });
    }

    return updateKeys({
      variables: {
        application,
        environment: environment.filter((key) => key !== value),
      },
    });
  };

  const { application = [], environment = [] } = data?.globalTagKeys ?? {};

  return (
    <>
      <BreadcrumbsBox title="Mapping Rules">
        <Can action={ACTIONS.updateMapping}>
          <FlexContainer justifyContent="flex-end">
            <MappingRuleSideButton onKeyAdd={refetch} />
          </FlexContainer>
        </Can>
      </BreadcrumbsBox>
      <Box spacing="spacing04">
        <Message status="notification" image="info">
          <FlexContainer direction="column" alignItems="start">
            <Text
              weight="medium"
              color={theme.color.text.info}
              mb={theme.spacing.spacing02}
            >
              Global mapping rules are the primary method of discovering
              applications and their environments.
            </Text>
            <Text mb={theme.spacing.spacing00} color={theme.color.text.text02}>
              You can define keys that will become application and environment
              names in the automatic discovery process. You can provide multiple
              keys. IBM Multicloud Accelerator will use these rules to attach
              resources to applications.
            </Text>
          </FlexContainer>
        </Message>
        <Spacer height={theme.spacing.spacing04} />
        <UniversalWrap
          errorProps={{ error }}
          loaderProps={{
            loading,
            Component: Loader,
          }}
        >
          <Table striped>
            <Table.thead>
              <Table.tr>
                <Table.th style={{ width: "7rem" }}>Type</Table.th>
                <Table.th>Tag Key</Table.th>
                <Table.th style={{ width: "1rem" }} />
              </Table.tr>
            </Table.thead>
            <Table.tbody>
              {application.map((appKey) => (
                <Table.tr key={`application${appKey ?? ""}`}>
                  <Table.td css={{ verticalAlign: "middle" }}>
                    {/*
                    // @ts-expect-error We shouldn't use custom colors, it may break dark/light theme */}
                    <Tag text="Application" color="#2F5B9D" />
                  </Table.td>
                  <Table.td css={{ verticalAlign: "middle" }}>
                    {appKey}
                  </Table.td>
                  <Table.td>
                    <Can action={ACTIONS.updateMapping}>
                      <Button
                        size="md"
                        severity="low"
                        icon="trash"
                        onClick={() => {
                          setIsModalConfirmOpen(true);
                          setDeleteData({
                            type: "application",
                            name: appKey ?? "",
                          });
                        }}
                      />
                    </Can>
                  </Table.td>
                </Table.tr>
              ))}
              {environment.map((envKey) => (
                <Table.tr key={`environment${envKey ?? ""}`}>
                  <Table.td css={{ verticalAlign: "middle" }}>
                    {/*
                    // @ts-expect-error We shouldn't use custom colors, it may break dark/light theme */}
                    <Tag text="Environment" color="#9B59B6" />
                  </Table.td>
                  <Table.td css={{ verticalAlign: "middle" }}>
                    {envKey}
                  </Table.td>
                  <Table.td style={{ textAlign: "right" }}>
                    <Can action={ACTIONS.updateMapping}>
                      <Button
                        size="md"
                        severity="low"
                        icon="trash"
                        onClick={() => {
                          setIsModalConfirmOpen(true);
                          setDeleteData({
                            type: "environment",
                            name: envKey ?? "",
                          });
                        }}
                      />
                    </Can>
                  </Table.td>
                </Table.tr>
              ))}
            </Table.tbody>
          </Table>
        </UniversalWrap>
      </Box>
      <ModalConfirm
        isOpen={isModalConfirmOpen}
        confirm={() => onSubmit(deleteData)}
        actionLabel="Delete"
        contentLabel="Delete Mapping Rule"
        onClose={() => setIsModalConfirmOpen(false)}
      >
        {generateActionConfirmText(deleteData.name)("delete")()}
      </ModalConfirm>
    </>
  );
}

function Loader() {
  return (
    <TableLoader
      gapY={10}
      gapX={6}
      rows={[
        {
          count: 6,
          height: 60,
          items: [
            {
              widthPercent: 10,
            },
            {
              widthPercent: 85,
            },
            {
              widthPercent: 5,
            },
          ],
        },
      ]}
    />
  );
}
